import { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UseIdleTimeoutOptions {
  timeoutMinutes?: number;
  onTimeout?: () => void;
  enabled?: boolean;
}

export function useIdleTimeout({
  timeoutMinutes = 10,
  onTimeout,
  enabled = true,
}: UseIdleTimeoutOptions = {}) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const [remainingSeconds, setRemainingSeconds] = useState(timeoutMinutes * 60);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleLogout = useCallback(async () => {
    // Clear session storage
    sessionStorage.removeItem('sessionActive');
    localStorage.removeItem('currentSessionId');
    
    // Show toast before logout
    toast.error('Session expired due to inactivity.', {
      duration: 5000,
    });

    // Custom callback if provided
    if (onTimeout) {
      onTimeout();
    }

    // Sign out from Supabase
    await supabase.auth.signOut();
  }, [onTimeout]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setRemainingSeconds(timeoutMinutes * 60);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (enabled) {
      timeoutRef.current = setTimeout(() => {
        handleLogout();
      }, timeoutMinutes * 60 * 1000);
    }
  }, [timeoutMinutes, enabled, handleLogout]);

  // Countdown interval to update remaining seconds
  useEffect(() => {
    if (!enabled) return;

    countdownRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remaining = Math.max(0, timeoutMinutes * 60 - elapsed);
      setRemainingSeconds(remaining);
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [enabled, timeoutMinutes]);

  useEffect(() => {
    if (!enabled) return;

    // Events to track for user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle the reset to avoid excessive calls
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    const throttledReset = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        resetTimer();
      }, 1000);
    };

    // Add event listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Start the initial timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, throttledReset);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [enabled, resetTimer]);

  return {
    resetTimer,
    lastActivity: lastActivityRef.current,
    remainingSeconds,
  };
}
